import type { RoadSection, RoadSectionId } from '../domain/Reference';
import { EMPTY_VALUE, formatAttribute } from './format';

// Pure rules of the referential selection on the map (see docs/map-v2/plan.md, Phase 2).

export type SelectionModifier = 'none' | 'toggle' | 'range';

// Ctrl or Cmd adds/removes one section; Shift selects a range along the axis.
export const modifierOf = (event: { ctrlKey?: boolean; metaKey?: boolean; shiftKey?: boolean }): SelectionModifier => {
    if (event.ctrlKey || event.metaKey) {
        return 'toggle';
    }

    return event.shiftKey ? 'range' : 'none';
};

export const toggleId = (ids: RoadSectionId[], id: RoadSectionId): RoadSectionId[] =>
    ids.includes(id) ? ids.filter((other) => other !== id) : [...ids, id];

// Every section of the axis between the anchor and the target, both included, in the order of the axis.
// Null when they are not on the same axis (or the anchor is unknown): there is no range to draw.
export const rangeOnAxis = (
    sections: RoadSection[],
    anchorId: RoadSectionId | null,
    targetId: RoadSectionId,
): RoadSectionId[] | null => {
    const anchor = sections.find((section) => section.id === anchorId);
    const target = sections.find((section) => section.id === targetId);
    if (!anchor || !target || anchor.roadName !== target.roadName) {
        return null;
    }
    const from = Math.min(anchor.cumulStart, target.cumulStart);
    const to = Math.max(anchor.cumulEnd, target.cumulEnd);

    return sections
        .filter(
            (section) => section.roadName === target.roadName && section.cumulStart >= from && section.cumulEnd <= to,
        )
        .sort((a, b) => a.cumulStart - b.cumulStart)
        .map((section) => section.id);
};

export type ExtendKind = 'axis' | 'category' | 'gestionary';
export const EXTEND_KINDS: readonly ExtendKind[] = ['axis', 'category', 'gestionary'];

const onAxisOf = (sections: RoadSection[], base: RoadSection): RoadSection[] =>
    sections.filter((section) => section.roadName === base.roadName).sort((a, b) => a.cumulStart - b.cumulStart);

// The sections a right-click "extend" action would select, always including the base section:
// the whole axis, the neighbors of the same category in an unbroken run, or the axis' sections that
// share the base section's gestionnaire.
export const extendSelection = (sections: RoadSection[], base: RoadSection, kind: ExtendKind): RoadSectionId[] => {
    const axis = onAxisOf(sections, base);
    if (kind === 'axis') {
        return axis.map((section) => section.id);
    }
    if (kind === 'gestionary') {
        return axis.filter((section) => section.gestionary === base.gestionary).map((section) => section.id);
    }
    const index = axis.findIndex((section) => section.id === base.id);
    let first = index;
    let last = index;
    while (first > 0 && axis[first - 1]?.category === base.category) {
        first -= 1;
    }
    while (last < axis.length - 1 && axis[last + 1]?.category === base.category) {
        last += 1;
    }

    return axis.slice(first, last + 1).map((section) => section.id);
};

export interface ExtendHint {
    kind: ExtendKind;
    // What the action keys on, shown next to its label: the axis' section count, the category, the gestionnaire.
    count: number;
    value: string;
}

export const extendHints = (sections: RoadSection[], base: RoadSection): ExtendHint[] => [
    { kind: 'axis', count: onAxisOf(sections, base).length, value: base.roadName },
    {
        kind: 'category',
        count: extendSelection(sections, base, 'category').length,
        value: base.category ?? EMPTY_VALUE,
    },
    {
        kind: 'gestionary',
        count: extendSelection(sections, base, 'gestionary').length,
        value: base.gestionary ?? EMPTY_VALUE,
    },
];

// Referential fields summarized for a multi-selection, in the order of the object sheet.
export const MULTI_ATTRIBUTE_FIELDS = [
    'category',
    'trucks',
    'urban',
    'roadType',
    'year',
    'width',
    'gestionary',
] as const;
export type MultiAttributeField = typeof MULTI_ATTRIBUTE_FIELDS[number];

export interface AttributeBreakdown {
    field: MultiAttributeField;
    // True when every selected section has the same value (possibly none).
    homogeneous: boolean;
    // The shared value when homogeneous, otherwise how many distinct values there are.
    value: string;
    distinct: number;
    // Each distinct value with its number of sections, the most frequent first.
    values: { value: string; count: number }[];
}

export const attributeBreakdowns = (sections: RoadSection[]): AttributeBreakdown[] =>
    MULTI_ATTRIBUTE_FIELDS.map((field) => {
        const counts = new Map<string, number>();
        sections.forEach((section) => {
            const label = formatAttribute(field, section[field]);
            counts.set(label, (counts.get(label) ?? 0) + 1);
        });
        const values = Array.from(counts.entries())
            .map(([value, count]) => ({ value, count }))
            .sort((a, b) => b.count - a.count || a.value.localeCompare(b.value, 'fr'));

        return {
            field,
            homogeneous: values.length <= 1,
            value: values.length === 1 ? values[0]!.value : EMPTY_VALUE,
            distinct: values.length,
            values,
        };
    });

const MAX_LISTED_AXES = 5;

// "D888, D36" or, past five axes, "D888, D36, D12, D45, D77 +3".
export const formatAxes = (axes: string[]): string =>
    axes.length <= MAX_LISTED_AXES
        ? axes.join(', ')
        : `${axes.slice(0, MAX_LISTED_AXES).join(', ')} +${axes.length - MAX_LISTED_AXES}`;
